/**
 * How each tool reads in the transcript.
 *
 * Verb pairs rather than raw tool names, because "Read Weekly review" tells
 * the user what happened and "read_note(01H8...)" does not. `write: true`
 * drives the distinct treatment: an edit to your notes should never be quiet.
 */
import {
  FilePlus2,
  FileText,
  FolderPlus,
  FolderTree,
  Info,
  MoveRight,
  PencilLine,
  Search,
  Tag,
  Trash2,
  Type,
  Wrench,
  type LucideIcon,
} from "lucide-react";

export interface ToolMeta {
  icon: LucideIcon;
  running: string;
  done: string;
  write: boolean;
}

const META: Record<string, ToolMeta> = {
  search_notes: { icon: Search, running: "Searching", done: "Searched", write: false },
  list_notes: { icon: FolderTree, running: "Listing", done: "Listed", write: false },
  read_note: { icon: FileText, running: "Reading", done: "Read", write: false },
  get_note_info: { icon: Info, running: "Checking", done: "Checked", write: false },
  create_note: { icon: FilePlus2, running: "Creating", done: "Created", write: true },
  edit_note: { icon: PencilLine, running: "Editing", done: "Edited", write: true },
  append_note: { icon: PencilLine, running: "Adding to", done: "Added to", write: true },
  rewrite_note: { icon: PencilLine, running: "Rewriting", done: "Rewrote", write: true },
  rename_note: { icon: Type, running: "Renaming", done: "Renamed", write: true },
  move_note: { icon: MoveRight, running: "Moving", done: "Moved", write: true },
  trash_note: { icon: Trash2, running: "Trashing", done: "Trashed", write: true },
  create_folder: { icon: FolderPlus, running: "Creating folder", done: "Created folder", write: true },
  set_note_tags: { icon: Tag, running: "Tagging", done: "Tagged", write: true },
};

const FALLBACK: ToolMeta = { icon: Wrench, running: "Working", done: "Done", write: false };

/** Tool names arrive prefixed by the MCP server they came from. */
export function metaFor(name: string): ToolMeta {
  return META[name.replace(/^mcp__notes__/, "")] ?? FALLBACK;
}

/** The note id a tool call was about, if it named one. */
export function targetNoteId(input: unknown): string | null {
  if (!input || typeof input !== "object") return null;
  const rec = input as Record<string, unknown>;
  const id = rec["note_id"] ?? rec["parent_id"];
  return typeof id === "string" ? id : null;
}

/** A short human label for what the call was aimed at. */
export function targetLabel(input: unknown): string {
  if (!input || typeof input !== "object") return "";
  const rec = input as Record<string, unknown>;
  if (typeof rec["query"] === "string") return `"${rec["query"]}"`;
  if (typeof rec["title"] === "string") return rec["title"];
  return "";
}
